import {
  callApiPatch,
  callApiPost,
  constructApiUrl,
  createParams,
  validateParams,
  getChangedParams,
  callApiGet,
  getPolicyID,
} from './common';
import {
  activationDateGet,
  createCustomer,
  updateCustomer,
  quoteCustomer,
  updateQuote,
  purchase,
  saveOffer,
  retrieveOffer,
  portraitLead,
} from '../../actions/Api';
import { createErrorBody } from '../error';
import { getPackagesWithCoverage, validateCoverageCode } from '../package/packageUtils';

const type = 'customer';

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingCustomer ? state.isUpdatingCustomer : true;
};

/**
 * Call the API using GET.
 * @param  {string}   service         The service to call.
 * @param  {Object}   params          The params for the service.
 * @param  {Function} dispatch        The dispatch function.
 * @param  {boolean}  externalService The dispatch function.
 * @param  {boolean}  useToken        The dispatch function.
 * @return {Promise}                  The Promise.
 */
const callCustomerApiGet = (
  service, params, dispatch, externalService = false, useToken = true
) => {
  const url = constructApiUrl(type, service);
  return callApiGet(url, params, dispatch, externalService, useToken);
};

/**
 * Call the API using POST.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callCustomerApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string} service The service to call.
 * @param  {Object} params  The params for the service.
 * @return {Promise}        The Promise.
 */
const callCustomerApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreateCustomer = (service, params) => {
  return dispatch => {
    dispatch(createCustomer());
    return callCustomerApiPost(service, params, dispatch);
  };
};

/**
 * Call the create service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestCreateCustomer = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const mappedParams = params ? createParams(params, formValues, getState()) : {};
      const { section: { packageData: { initialValues: { consumerId } } } } = getState();
      mappedParams.cpr = mappedParams.cpr.replace('-', '');
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }

      if (consumerId) {
        mappedParams.consumerId = consumerId;
      }
      const service = '';
      return dispatch(callCreateCustomer(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateCustomer = (service, params) => {
  return dispatch => {
    dispatch(updateCustomer());
    return callCustomerApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateCustomer = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      let mappedParams = params ? createParams(params, formValues, state) : {};
      const { userData: { user: { customerID } } } = state;
      if (state.userData.user) {
        // Find the changed values and pass those along.
        const additionalParams = { customerID };
        mappedParams =
          getChangedParams(mappedParams, state.userData.user.usedParams, ['customerID'],
            additionalParams);
      }
      const service = `${customerID}`;
      return dispatch(callUpdateCustomer(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the quote action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @param  {boolean} onlyMinData Only return minDate.
 * @return {function} Dispatch function.
 */
const callGetActivationDate = (service, params, onlyMinData) => {
  return dispatch => {
    dispatch(activationDateGet());
    return callCustomerApiPatch(service, params, dispatch).then(data => {
      if (!data.activationDate) {
        return Promise.reject('Missing date info.');
      }
      if (data.activationDate.key) {
        if (onlyMinData) {
          return {
            minActivationDate: data.activationDate.key,
          };
        }
        return {
          activationDate: data.activationDate.key,
          minActivationDate: data.activationDate.key,
          activationDateText: data.activationDate.value,
        };
      }
      return data;
    });
  };
};

/**
 * Call the quote service.
 * @return {Promise}           The promise of the service.
 */
export const requestGetActivationDate = (onlyMinData) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Get customer ID. Fail if none is found.
      if (!state.userData.user || !state.userData.user.customerID) {
        return Promise.reject(createErrorBody('Missing parameters', 'customerID'));
      }
      const { userData, userData: { basketList, user: { customerID } } } = state;

      // Use basketList to fetch the policyIDs we need.
      let failed = [];
      const policyIDs = basketList.map(entry => {
        const policyID = getPolicyID(userData, entry);
        if (!policyID) {
          failed = [...failed, entry];
        }
        return policyID;
      });
      if (failed.length > 0) {
        return Promise.reject(createErrorBody('Missing policyID(s)', failed));
      }
      const params = {
        customerID,
        policyQuotes: policyIDs,
      };
      const service = `${customerID}/activationDate`;
      return dispatch(callGetActivationDate(service, params, onlyMinData));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Call the get activation dates.
 * @return {Promise}     The promise of the service.
 */
export const getActivationDates = (formValues, dispatch) => {
  const { basketList, user: { customerID }, addNextDayToStartDateList } = formValues;
  const product = basketList[0];
  const policyID = getPolicyID(formValues, product);
  const service = addNextDayToStartDateList ? `${customerID}/activationDates/${policyID}/${addNextDayToStartDateList}` : `${customerID}/activationDates/${policyID}`;
  return callCustomerApiGet(service, {}, dispatch, false, false).then(data => {
    const newData = data;
    newData[0].value = `${data[0].value} (Hurtigst muligt)`;
    return newData;
  });
};

/**
 * Dispatches the quote action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callQuoteCustomer = (service, params) => {
  return dispatch => {
    return callCustomerApiPost(service, params, dispatch).then(data => {
      if (!data.totalOfferedPrice) {
        return Promise.reject('Missing price info.');
      }
      const newData = data;
      if (Object.hasOwnProperty.call(params, 'packageId')) {
        newData.packageId = params.packageId;
      }
      return newData;
    });
  };
};

/**
 * Call the quote service.
 * @return {Promise}           The promise of the service.
 */
export const requestCustomerQuote = () => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Get customer ID. Fail if none is found.
      if (!state.userData.user || !state.userData.user.customerID) {
        return Promise.reject(createErrorBody('Missing parameters', 'customerID'));
      }
      const { userData: { basketList, user: { customerID } } } = state;
      const packageInfo = getPackagesWithCoverage(state);

      // Use basketList to fetch the policyIDs we need.
      let failed = [];
      const policyIDs = [];
      basketList.every(entry => {
        if (packageInfo.length > 0) {
          packageInfo.every(element => {
            if (!state.userData[entry] || !state.userData[entry][element.packageId]
              || !state.userData[entry][element.packageId].policyID) {
              failed = [...failed, entry];
              return false;
            }
            const response = {};
            response[element.packageId] = {};
            response[element.packageId].policyID =
              state.userData[entry][element.packageId].policyID;
            // Use UserData package defaults if available.
            response[element.packageId].coverageCodes =
              state.userData[element.packageId] && state.userData[element.packageId].coverageCodes ?
                state.userData[element.packageId].coverageCodes :
                element.coverageCodes;
            policyIDs.push(response);
            return true;
          });

          return true;
        }

        // If one policyID is missing we fail.
        if (!state.userData[entry] || !state.userData[entry].policyID) {
          failed = [...failed, entry];
          return false;
        }

        policyIDs.push(state.userData[entry].policyID);
        return true;
      });

      if (failed.length > 0) {
        return Promise.reject(createErrorBody('Missing policyID(s)', failed));
      }
      dispatch(quoteCustomer());
      const service = `${customerID}/quote`;
      const serviceCalls = [];
      if (packageInfo.length > 0) {
        let delay = 0;
        // Fix for OE-5635
        // Update parallel price calculation to one serial and three parallel
        const firstRequestItem = packageInfo[0];
        const response = {};
        const updateAllPolicyQuotes = { consent: false };
        // First Request
        return dispatch(callQuoteCustomer(service, {
          customerID,
          policyQuotes: policyIDs.filter(e => e[firstRequestItem.packageId])
            .map(e => e[firstRequestItem.packageId].policyID),
          coverageCodes: policyIDs.filter(e => e[firstRequestItem.packageId])
            .reduce((result, e) => e[firstRequestItem.packageId].coverageCodes, []),
          packageId: firstRequestItem.packageId,
        })).then(responseData => {
          response[responseData.packageId] = responseData;
          // Remaining 3 requests
          packageInfo.forEach((item, index) => {
            if (index > 0) {
              serviceCalls.push(dispatch(new Promise((resolve) => {
                setTimeout(() => {
                  resolve(callQuoteCustomer(service, {
                    customerID,
                    policyQuotes: policyIDs.filter(e => e[item.packageId])
                      .map(e => e[item.packageId].policyID),
                    coverageCodes: policyIDs.filter(e => e[item.packageId])
                      .reduce((result, e) => e[item.packageId].coverageCodes, []),
                    packageId: item.packageId,
                    updateAllPolicyQuotes,
                  }));
                }, delay);
              })));
              delay += 100;
            }
          });
          return Promise.all(serviceCalls).then(data => {
            // TPH150-8878 Single price/pacakge pefo senior
            if (data.length > 0) {
              data.forEach(element => {
                response[element.packageId] = element;
              });
            }
            return response;
          });
        });
      }
      return dispatch(callQuoteCustomer(service, {
        customerID,
        policyQuotes: policyIDs,
      }));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateQuote = (service, parameters) => {
  const params = parameters;
  let id = null;
  if (Object.hasOwnProperty.call(params, 'packageId')) {
    const { packageId } = params;
    id = packageId;
    delete params.packageId;
  }
  return dispatch => {
    return callCustomerApiPatch(service, params, dispatch).then(data => {
      const newData = data;
      if (id) {
        newData.packageId = id;
      }
      return newData;
    });
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {string} product    The product to call the updated quote for.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateQuote = (params, product, formValues = {}) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      const { userData: { user: { customerID }, packageId } } = state;
      const { section: { packageData: { initialValues } }, userData } = state;
      const mappedParams = params
        ? createParams(params, { ...formValues, ...state.userData }, state, product, { customerID })
        : {};

      if (product) {
        const productData = state.userData[product];
        let packageInfo = getPackagesWithCoverage(state);
        dispatch(updateQuote());
        if (packageInfo.length > 0) {
          const serviceCalls = [];
          if (packageId) {
            packageInfo = packageInfo.filter(item => item.packageId === packageId);
          }

          let delay = 0;
          packageInfo.forEach(item => {
            const { policyID } = productData[item.packageId];
            const service = `${customerID}/quote/${product}/${policyID}`;
            const additionalParams = {};
            if (!packageId && !mappedParams.coverageCodes) {
              const {
                userData: {
                  quotes: {
                    [item.packageId]: {
                      usedParams: { coverageCodes },
                    },
                  },
                },
              } = state;
              if (coverageCodes) {
                let newCoverageCodes = item.coverageCodes.concat(coverageCodes);
                newCoverageCodes = newCoverageCodes.reduce((result, current) => {
                  return result.indexOf(current) !== -1 ? result : result.concat(current);
                }, []);
                newCoverageCodes = newCoverageCodes.filter(coverageCode =>
                  validateCoverageCode({ code: coverageCode }, initialValues, userData)
                  && item.removedCoverages.indexOf(coverageCode) === -1);
                additionalParams.coverageCodes = newCoverageCodes;
              }
            }
            serviceCalls.push(dispatch(new Promise((resolve) => {
              setTimeout(() => {
                resolve(callUpdateQuote(service, {
                  ...mappedParams,
                  ...additionalParams,
                  policyID,
                  packageId: item.packageId,
                }));
              }, delay);
            })));
            delay += 300;
          });
          return Promise.all(serviceCalls).then(data => {
            const response = {};
            data.forEach(element => {
              response[element.packageId] = element;
            });
            return response;
          });
        }
        const { policyID } = productData;
        const service = `${customerID}/quote/${product}/${policyID}`;
        return dispatch(callUpdateQuote(service, mappedParams));
      }
      // Update that will effect all products.
      dispatch(updateQuote());
      const service = `${customerID}/quote`;
      return dispatch(callUpdateQuote(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the quote action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPurchase = (service, params) => {
  return dispatch => {
    dispatch(purchase());
    return callCustomerApiPost(service, params, dispatch);
  };
};

/**
 * Call the quote service.
 * @return {Promise}           The promise of the service.
 */
export const requestPurchase = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Get customer ID. Fail if none is found.
      if (!state.userData.user || !state.userData.user.customerID) {
        return Promise.reject(createErrorBody('Missing parameters', 'customerID'));
      }
      const { userData, userData: { basketList, user: { customerID } } } = state;

      // Let check email permission flag.
      const extraData = {
        emailPermissionForPromotion: false,
      };
      if (state.userData.emailPermissionForPromotion) {
        extraData.emailPermissionForPromotion = true;
      }
      // Use basketList to fetch the policyIDs we need.
      let failed = [];
      const policyIDs = basketList.map(entry => {
        const policyID = getPolicyID(userData, entry);
        if (!policyID) {
          failed = [...failed, entry];
        }
        return policyID;
      });
      if (failed.length > 0) {
        return Promise.reject(createErrorBody('Missing policyID(s)', failed));
      }
      const paramsForService = {
        ...createParams(params, formValues, getState(), '', extraData),
        customerID,
        policyQuotes: policyIDs,
      };
      const service = `${customerID}/purchase`;
      return dispatch(callPurchase(service, paramsForService));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the quote action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callSaveOffer = (service, params) => {
  return dispatch => {
    dispatch(saveOffer());
    return callCustomerApiPost(service, params, dispatch);
  };
};

/**
 * Dispatches the portrait lead action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPortraitLead = (service, params) => {
  return dispatch => {
    dispatch(portraitLead());
    return callCustomerApiPost(service, params, dispatch);
  };
};


/**
 * Call the quote service.
 * @return {Promise}           The promise of the service.
 */
export const requestSaveOffer = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Get customer ID. Fail if none is found.
      if (!state.userData.user || !state.userData.user.customerID) {
        return Promise.reject(createErrorBody('Missing parameters', 'customerID'));
      }
      const { userData, userData: { basketList, user: { customerID } } } = state;

      // Let check email permission flag.
      const extraData = {
        emailPermissionForPromotion: false,
      };
      if (state.userData.emailPermissionForPromotion) {
        extraData.emailPermissionForPromotion = true;
      }
      // Use basketList to fetch the policyIDs we need.
      let failed = [];
      const policyIDs = basketList.map(entry => {
        const policyID = getPolicyID(userData, entry);
        if (!policyID) {
          failed = [...failed, entry];
        }
        return policyID;
      });
      if (failed.length > 0) {
        return Promise.reject(createErrorBody('Missing policyID(s)', failed));
      }
      const paramsForService = {
        ...createParams(params, formValues, getState(), '', extraData),
        customerID,
        policyQuotes: policyIDs,
      };
      const service = `${customerID}/offer`;
      return dispatch(callSaveOffer(service, paramsForService));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the quote action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callRetrieveOffer = (service, params) => {
  return dispatch => {
    dispatch(retrieveOffer());
    const url = constructApiUrl('', service);
    return callApiGet(url, params, dispatch, false, true);
  };
};

/**
 * Call the quote service.
 * @return {Promise}           The promise of the service.
 */
export const requestRetrieveOffer = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const paramsForService = {
        ...createParams(params, formValues, getState()),
      };
      const { section: { packageData: { initialValues: { consumerId } } } } = getState();
      const { quoteId, cpr } = paramsForService;
      const service = `offer/${quoteId}/${cpr.replace('-', '')}/${consumerId}`;
      return dispatch(callRetrieveOffer(service, {}));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Call the portrait lead service.
 * @return {Promise}           The promise of the service.
 */
export const requestPortraitLead = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const paramsForService = {
        ...createParams(params, formValues, getState(), ''),
      };
      const service = 'generateLead';
      return dispatch(callPortraitLead(service, paramsForService));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/customer.js